import { styled } from '@linaria/react';
import { useTranslation } from 'react-i18next';
import { downloadCSV } from '../../../../helpers/csv-download';
import { formatDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { useGlobalStore } from '../../../../store/global-store/global-store.ts';
import { newCOLORS } from '../../../../styles/colors';
import { skyDark, skyLighter } from '../../../../styles/design-tokens.ts';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip.tsx';
import { FloraButton } from '../../../flora/flora-button.tsx';
import { WorkPeriodChartHeader } from '../../components/work-period-chart-header.tsx';
import type { MeasureMetadata, WorkPeriod } from '../../work-periods.type';
import { getTrendColor } from '../helpers/comparison-table.helpers';
import type { DesiredTrendlineDirection } from '../helpers/comparison-table.type';
import {
  formatWorkPeriodDataForFlora,
  getLastValue,
  getUnitsLabel,
  sortWorkPeriodsByStartDate,
} from '../helpers/measure-comparison-chart.helpers.ts';

interface ComparisonTableProps {
  customMeasures: MeasureMetadata[];
  measureData: Record<string, any>;
  workPeriods: WorkPeriod[];
}

export const ComparisonTable = ({ customMeasures, measureData, workPeriods }: ComparisonTableProps) => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);

  // Calculate trend indicators with dynamic colors based on desired_trendline_direction
  const getTrendIndicator = (
    currentValue: number,
    previousValue: number,
    desiredDirection: DesiredTrendlineDirection | undefined,
  ) => {
    const color = getTrendColor(currentValue, previousValue, desiredDirection);

    if (currentValue > previousValue) {
      return <TrendIcon name="trending_up" size={16} color={color} />;
    } else if (currentValue < previousValue) {
      return <TrendIcon name="trending_down" size={16} color={color} />;
    }
    return <TrendIcon name="trending_flat" size={16} color={color} />;
  };

  // Get next period value for trend calculation
  // With the new sorting order, oldest periods are first and newest are last
  const getNextValue = (measure: string, currentPeriodIndex: number) => {
    const sortedWorkPeriods = sortWorkPeriodsByStartDate(workPeriods);
    if (currentPeriodIndex <= 0) return null;

    const previousPeriod = sortedWorkPeriods[currentPeriodIndex - 1];
    const value = measureData[previousPeriod.id]?.[measure];
    if (!value) return null;

    const dates = Object.keys(value).sort();
    if (dates.length === 0) return null;

    const lastDate = dates[dates.length - 1];
    return value[lastDate];
  };

  const handleDownload = () => {
    const sortedWorkPeriods = sortWorkPeriodsByStartDate(workPeriods);
    try {
      const csvData = [
        ['Measure', ...sortedWorkPeriods.map((wp) => wp.name)],
        ...customMeasures.map((measure) => [
          measure.measure_title,
          ...sortedWorkPeriods.map((workPeriod) => getLastValue(measureData[workPeriod.id]?.[measure.measure_name])),
        ]),
      ];

      downloadCSV(csvData as (string | number)[][], 'sprint-comparison.csv');
    } catch (error) {
      console.error('Error generating CSV data:', error);
    }
  };

  const floraData = formatWorkPeriodDataForFlora(measureData, customMeasures, workPeriods);

  return (
    <div>
      <WorkPeriodChartHeader
        title={`${i18n.t('common.sprint')} Comparison`}
        onExport={handleDownload}
        hideRadio
        floraButton={
          useAI && (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t('flora.work_periods.sprint_comparison_table_prompt')}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <TableHeader>Measure</TableHeader>
              <TableHeader>Average</TableHeader>
              {sortWorkPeriodsByStartDate(workPeriods).map((wp) => (
                <TableHeader key={wp.id}>
                  <HeaderContainer>
                    <SprintName>{wp.name}</SprintName>
                    <DateRange>
                      {formatDate(wp.start_date, { format: 'MMM D' })} - {formatDate(wp.end_date, { format: 'MMM D' })}
                    </DateRange>
                  </HeaderContainer>
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {customMeasures.map((measure) => (
              <tr key={measure.measure_name}>
                <TableCell>
                  <CellContentWrapper>
                    <Tooltip
                      label={measure.measure_description}
                      position="right"
                      withArrow
                      multiline
                      maw={250}
                      variant="black"
                    >
                      <MeasureLabelContainer>
                        <MeasureLabel>{measure.measure_title}</MeasureLabel>
                        <Icon name="info" size={14} color={skyDark} />
                      </MeasureLabelContainer>
                    </Tooltip>
                  </CellContentWrapper>
                </TableCell>
                <TableCell>
                  <CellContentWrapper>
                    {(() => {
                      const values = sortWorkPeriodsByStartDate(workPeriods)
                        .map((workPeriod) => {
                          const value = measureData[workPeriod.id]?.[measure.measure_name];
                          if (!value) return null;
                          const lastValue = getLastValue(value);
                          return lastValue === null ? null : parseFloat(lastValue.toString());
                        })
                        .filter((v): v is number => v !== null);

                      if (values.length === 0) return '-';
                      const avg = values.reduce((a, b) => a + b, 0) / values.length;
                      const formattedAvg = measure.measure_units === 'Percentage' ? avg.toFixed(1) : avg.toFixed(0);

                      return (
                        <>
                          <span>{formattedAvg}</span>
                          <UnitLabel>{getUnitsLabel(measure.measure_units, i18n.t)}</UnitLabel>
                        </>
                      );
                    })()}
                  </CellContentWrapper>
                </TableCell>
                {sortWorkPeriodsByStartDate(workPeriods).map((workPeriod, index) => {
                  const value = measureData[workPeriod.id]?.[measure.measure_name];
                  const displayValue = getLastValue(value);
                  const numericValue = displayValue !== null ? parseFloat(displayValue.toString()) : null;

                  const previousValue = getNextValue(measure.measure_name, index);
                  const showTrend = numericValue !== null && previousValue !== null;

                  return (
                    <TableCell key={workPeriod.id}>
                      <CellContentWrapper>
                        <ValueContainer>
                          <span>{displayValue}</span>
                          <UnitLabel>{getUnitsLabel(measure.measure_units, i18n.t)}</UnitLabel>
                          {showTrend &&
                            getTrendIndicator(numericValue!, previousValue!, measure.desired_trendline_direction)}
                        </ValueContainer>
                      </CellContentWrapper>
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </div>
  );
};

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SprintName = styled.div`
  font-weight: 600;
  color: ${newCOLORS.black2};
  font-size: 14px;
  line-height: 1.4;
`;

const DateRange = styled.div`
  font-size: 12px;
  color: ${newCOLORS.black2};
  font-weight: 500;
  line-height: 1.3;
`;

const TableContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow: hidden;
  margin-top: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  padding: 12px 30px;
  text-align: left;
  border: 1px solid #e3e5e5;
  color: #202325;
  font-weight: 600;
  font-size: 14px;
  background-color: ${skyLighter};

  &:first-child {
    border-left: 1px solid #e3e5e5;
  }

  &:last-child {
    border-right: 1px solid #e3e5e5;
  }
`;

const TableCell = styled.td`
  padding: 12px 30px;
  text-align: left;
  border: 1px solid #e3e5e5;
  border-top: none;
  color: #202325;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.4;
  vertical-align: middle;
  position: relative;

  &:first-child {
    border-left: 1px solid #e3e5e5;
  }

  &:last-child {
    border-right: 1px solid #e3e5e5;
  }
`;

const CellContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
  
  & > span {
    font-size: 14px;
    font-weight: 500;
    color: ${newCOLORS.black2};
  }
`;

const MeasureLabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MeasureLabel = styled.div`
  font-weight: 600;
  color: ${newCOLORS.black2};
  font-size: 14px;
  line-height: 1.4;
`;
const TrendIcon = styled(Icon)`
  margin-left: 4px;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  color: ${newCOLORS.black2};
  
  & > span {
    font-size: 14px;
    font-weight: 500;
    color: ${newCOLORS.black2};
  }
`;

const UnitLabel = styled.div`
  color: ${skyDark};
  background-color: transparent;
  text-align: left;
  font-family: Figtree, sans-serif;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.7;
  padding-left: 2px;

  &:before {
    content: '';
    margin-right: ${(props) => (props.children === '%' ? '-4px' : '0px')};
  }
`;
